import { ButtonGroup, Flex, Heading, mediaBreakpointMixin, PageContent, token } from '@zillow/constellation';
import { useEffect } from 'react';
import LinkButton from '../../../components/LinkButton';
import { ZTSS_ENTER_YOUR_NAME, ZTSS_PROPERTIES } from '../../../constants/paths';
import styled from 'styled-components';
import ValueProp from './ValueProp';
import FAQ from './FAQ';
import images from '../../../images/ztss';
import useQueryParams from '../../../hooks/useQueryParams';
import {
  emitZtssStartScreeningTenantsEvent,
  emitZtssViewApplicationsEvent,
  emitZtssViewLandingPageEvent,
} from '../../../../client/analytics/clickstreamEvents';
import { useListingUser } from '../../../hooks/useListingUser';

const SECTION_MAX_WIDTH = '816px';

const StyledHeroSection = styled(PageContent)`
  position: relative;
  background: ${token('colors.coolGray100')};
  max-width: none;
  margin: 0;
  height: 400px;

  @media ${mediaBreakpointMixin('md')} {
    height: 481px;
  }
  @media ${mediaBreakpointMixin('lg')} {
    height: 562px;
  }
`;

const StyledHeroImage = styled.div`
  position: absolute;
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  height: 100%;
  z-index: 0;
  position: absolute;
  top: 0px;
  left: 0px;
  background-image: url(${images.hero});
  background-position: center;
  filter: brightness(0.55);
  @media ${mediaBreakpointMixin('md')} {
    height: 481px;
  }
  @media ${mediaBreakpointMixin('lg')} {
    height: 562px;
  }
`;

const HeroContentWrapper = styled(Flex)`
  z-index: 1;
  position: relative;
  text-align: center;
  max-width: ${SECTION_MAX_WIDTH};
`;

const StyledButtonWrapper = styled(ButtonGroup)`
  width: 243px;
`;

const StyledValuePropSection = styled(Flex)`
  padding: 0 ${token('spacing.sm')}px ${token('spacing.md')}px;
  margin: 0 auto;
  text-align: center;
  @media ${mediaBreakpointMixin('sm')} {
    padding: ${token('spacing.xl')}px ${token('spacing.sm')}px;
  }
  @media (min-width: 800px) {
    padding: 80px ${token('spacing.sm')}px;
    flex-direction: row;
    align-items: flex-start;
  }
`;

const StyledFaqSection = styled(Flex)`
  padding: 0 ${token('spacing.xs')}px;
  max-width: ${SECTION_MAX_WIDTH};
  margin: 0 auto;
  @media ${mediaBreakpointMixin('lg')} {
    padding: 0 0 80px;
  }
`;

export default function LandingPage() {
  const { utm_source, utm_content } = useQueryParams();
  const { user } = useListingUser();

  useEffect(() => {
    emitZtssViewLandingPageEvent(user, { utm_source_nm: utm_source, utm_content_nm: utm_content });
    if (utm_source && utm_content) {
      localStorage?.setItem('sia_utm_source', `_${utm_source}`);
      localStorage?.setItem('sia_utm_content', `_${utm_content}`);
    } else {
      localStorage?.removeItem('sia_utm_source');
      localStorage?.removeItem('sia_utm_content');
    }
  }, [utm_source, utm_content]);
  return (
    <div id="ztss-landing-page">
      <StyledHeroSection display="flex" alignItems="center" justifyContent="center">
        <StyledHeroImage />
        <HeroContentWrapper display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <Heading
            level={2}
            fontType={{ default: 'h3', md: 'h2' }}
            fontFamily="serif"
            fontColor="white"
            marginBottom={{ default: 'sm', md: 'md' }}
          >
            Save time with all-in-one tenant&nbsp;screening
          </Heading>
          <Heading
            level={5}
            fontType={{ default: 'h6', md: 'h5' }}
            fontColor="white"
            marginBottom={{ default: 'sm', md: 'md' }}
          >
            Zillow applications include residence history, background and credit checks, and eliminate the hassle of
            collecting fees.
          </Heading>
          <StyledButtonWrapper direction="column">
            <LinkButton
              buttonType="primary"
              to={ZTSS_ENTER_YOUR_NAME}
              marginBottom="sm"
              onClick={() => emitZtssStartScreeningTenantsEvent(user)}
            >
              Start screening applicants
            </LinkButton>
            <LinkButton
              buttonType="secondary"
              inverse
              to={ZTSS_PROPERTIES}
              marginBottom="sm"
              onClick={() => emitZtssViewApplicationsEvent(user)}
            >
              View applications
            </LinkButton>
          </StyledButtonWrapper>
        </HeroContentWrapper>
      </StyledHeroSection>
      <StyledValuePropSection
        display="flex"
        gap="lg"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <ValueProp
          title="Get a complete tenant overview"
          description="Our online application includes residence history, credit and background checks, eviction history, employment verification, and more."
          imgSrc={images.valuePropForm}
          imgAlt="Form icon"
        />
        <ValueProp
          title="No need to pull separate background or credit checks"
          description="Each application includes credit checks, eviction and criminal history, and more."
          imgSrc={images.valuePropMagnify}
          imgAlt="Magnifying glass icon"
        />
        <ValueProp
          title="Never track down an application fee again"
          description="Applicants pay an affordable screening fee on Zillow, so applications are hassle-free for you."
          imgSrc={images.valuePropPiggyBank}
          imgAlt="Piggy bank icon"
        />
      </StyledValuePropSection>
      <StyledFaqSection display="flex" flexDirection="column" justifyContent="center">
        <FAQ />
      </StyledFaqSection>
    </div>
  );
}
